import builder from '@builder.io/react';
import { BUILDER_API_KEY } from './environment';
import { checkIfCustomer, recordCustomEventToNewRelic } from './helpers';
import { ShippingCountryState } from './shipping';
import { pickBy } from 'lodash-es';
import { pathWithoutLocale } from './localization';
import { SHOP_LOCALE } from './constants';

const isNotNullOrUndefinedOrEmpty = (value: unknown) => {
  return value !== null && value !== undefined && value !== '';
};
export const getSharedUserTargetingAttributes = () => {
  const isLoggedIn = checkIfCustomer();
  const countryCode = ShippingCountryState.countryCode ?? 'US';
  const isApp = navigator.userAgent.toLowerCase().includes('aloios');
  const { host, device = 'desktop' } = builder.getUserAttributes() ?? {};
  const customTargetingAttribute = window.getSiteSettings?.().builderCustomTargetingAttribute;

  const urlPath = pathWithoutLocale(window.location.pathname);

  return pickBy(
    {
      urlPath,
      host,
      device,
      isLoggedIn,
      countryCode,
      isApp,
      contentSemantic: customTargetingAttribute,
    },
    isNotNullOrUndefinedOrEmpty
  );
};

type BuilderValue = string | number | boolean | string[] | number[] | boolean[] | undefined;

// ? [REVISIT] type this
export const fetchDataFromBuilder = async <T extends unknown>(
  modelname: string,
  {
    additionalUserAttributes,
    options,
    reportIfEmpty = false,
  }: {
    additionalUserAttributes?: Record<string, BuilderValue>;
    options?: Record<string, unknown>;
    reportIfEmpty?: boolean;
  } = {}
) => {
  const data: BuilderResponse<T> = await builder.get(modelname, {
    apiKey: BUILDER_API_KEY,
    options: pickBy(
      {
        ...options,
        includeUnpublished: window.getSiteSettings?.().includeUnpublishedBuilderContent ?? false,
      },
      isNotNullOrUndefinedOrEmpty
    ),
    userAttributes: {
      ...getSharedUserTargetingAttributes(),
      ...pickBy(additionalUserAttributes, isNotNullOrUndefinedOrEmpty),
    },
  });

  if (reportIfEmpty && !data) {
    recordCustomEventToNewRelic('builder_empty_response', {
      modelname,
      additionalUserAttributes,
      options,
      urlPath: pathWithoutLocale(window.location.pathname),
    });
  }

  return data;
};

export const getBuilderSharedDataAndOptions = (
  contentEntryId: string | undefined,
  additionalUserAttributes?: Record<string, BuilderValue>,
  optionsOverrides?: Record<string, BuilderValue>
) => {
  return {
    data: getSharedUserTargetingAttributes(),
    options: pickBy(
      {
        key: contentEntryId,
        entry: contentEntryId,
        locale: SHOP_LOCALE,
        ...optionsOverrides,
        userAttributes: {
          ...getSharedUserTargetingAttributes(),
          ...pickBy(additionalUserAttributes, isNotNullOrUndefinedOrEmpty),
        },
        options: {
          includeUnpublished: window.getSiteSettings?.().includeUnpublishedBuilderContent ?? false,
        },
      },
      isNotNullOrUndefinedOrEmpty
    ),
  };
};

export const getBuilderCacheBusterKey = () => {
  const themeID = window.Shopify?.theme?.id || '';
  return `${themeID}-cb-${window.getSiteSettings()?.builderCacheKey}`;
};

// Builder Types
export type BuilderResponse<T> = {
  createdBy: string;
  createdDate: number;
  data: T;
  folders: unknown[];
  id: string;
  lastUpdatedBy: string;
  meta: Meta;
  modelId: string;
  name: string;
  published: string;
  query: unknown[];
  testRatio: number;
  variations: Variations;
  lastUpdated: number;
  firstPublished: number;
  rev: string;
  variationId: string;
};

export type Meta = {
  format: string;
  kind: string;
  lastPreviewUrl: string;
  shopifyCustomDomain: string;
  shopifyDomain: string;
};

export type Variations = {};
